import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { setDoctor } from '../../features/doctor/doctorSlice';
import config from '../../config';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';

// Provider portal login for doctors AND nurses. Accounts are provisioned by an admin
// (doctors) or by an admin / doctor (nurses), never through public sign-up.
//
// How the role is decided: the doctor login is tried first. Only when it answers
// "invalid email or password" (401) do we try the nurse login. Both failing shows the same
// generic message, so the page never reveals which kind of account an email belongs to.
const DoctorLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const toast = (title, text, icon, timer = 2000) =>
    Swal.fire({ title, text, icon, showConfirmButton: false, timer });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const credentials = { email: formData.email, password: formData.password };

    try {
      // 1) doctor login
      try {
        const response = await axios.post(`${config.baseURL}/api/doctor-auth/login`, credentials);

        if (response.data.success) {
          dispatch(setDoctor({
            doctorInfo: response.data.doctor,
            token: response.data.token,
          }));

          // Same pattern as the patient login — persist separately from the
          // patient token so a doctor and patient session can't collide if
          // someone has both open in the same browser.
          localStorage.setItem('doctor_token', response.data.token);
          // a doctor login replaces any nurse session left in this browser
          localStorage.removeItem('nurse_token');
          localStorage.removeItem('nurse_info');

          await toast('Success', 'Logged in successfully!', 'success');
          navigate('/doctor/dashboard');
          return;
        }
      } catch (doctorError) {
        // Anything except "wrong email or password" is final (inactive account, server error…)
        if (doctorError.response?.status !== 401) throw doctorError;
      }

      // 2) nurse login
      const nurseRes = await axios.post(`${config.baseURL}/api/nurse-auth/login`, credentials);
      if (nurseRes.data.success) {
        localStorage.setItem('nurse_token', nurseRes.data.token);
        localStorage.setItem('nurse_info', JSON.stringify(nurseRes.data.nurse));
        // a nurse login replaces any doctor session left in this browser
        localStorage.removeItem('doctor_token');

        await toast('Success', 'Logged in successfully!', 'success');
        navigate('/nurse/dashboard');
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 403) {
        await toast(
          'Account Inactive',
          error.response.data.message || 'This account is inactive. Contact admin.',
          'warning',
          2500
        );
      } else if (status === 401) {
        await toast('Unauthorized', 'Invalid email or password.', 'error');
      } else if (status === 429) {
        await toast('Too many attempts', error.response.data.error || 'Please try again in a few minutes.', 'warning', 2500);
      } else {
        await toast('Login Failed', 'Something went wrong. Please try again.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  return (
    <>
    <Header/>
    <section className="login-part doctor-login-part">
      <div className="container">
        <div className="row" style={{ justifyContent: 'center' }}>
          <div className="col-md-6">
            <div className="login-box">
              <div className="login-title">Provider Sign In</div>
              <p style={{ color: '#888', marginBottom: 20, fontSize: 14 }}>
                Future Family provider portal for doctors and nurses
              </p>

              <form onSubmit={handleSubmit}>
                <div className="form-group email-box">
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter E-Mail ID"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'is-invalid' : ''}
                    autoComplete="username"
                  />
                  {errors.email && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      {errors.email}
                    </div>
                  )}
                </div>

                <div className="form-group password-box">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Enter Password"
                    value={formData.password}
                    onChange={handleChange}
                    className={errors.password ? 'is-invalid' : ''}
                    autoComplete="current-password"
                  />
                  <span
                    className="toggle-eye"
                    onClick={togglePasswordVisibility}
                    style={{ cursor: 'pointer' }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </span>
                  {errors.password && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      {errors.password}
                    </div>
                  )}
                </div>

                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'LOGGING IN...' : 'LOGIN'}
                </button>

                <div className="helper-links">
                  Doctor accounts are created by an admin. Nurse accounts are created by an
                  admin or your doctor — contact support if you don't have credentials yet.
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
    <Footer/>
    </>
  );
};

export default DoctorLogin;